<script setup>
import { ref, computed, onMounted } from 'vue';
import { ChevronsUpDown } from 'lucide-vue-next';
import { useFrontegg } from '@frontegg/vue';
import { useUserStore } from '@/stores/user.store.js';
import { useRouter } from 'vue-router';
import Input from '@/components/ui/input/Input.vue';

import {
    DropdownMenu,
    DropdownMenuTrigger,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuLabel
} from '@/components/ui/dropdown-menu';
import { SidebarMenuButton, useSidebar } from '@/components/ui/sidebar';
import { Badge } from '@/components/ui/badge';
import { useServicesStore } from '@/stores/services.store.js';
import { useLoaderStore } from '@/stores/loader.store.js';
import streamkapIconWhite from '@/assets/images/streamkap-icon-white.svg';
import { SkeletonLoader } from '../ui/skeleton-loader';

const props = defineProps({
    showServiceBadge: { type: Boolean, default: true },
    showStreamkapIcon: { type: Boolean, default: true }
});

// Tenant switching based on Frontegg auth state and user store
const { isMobile } = useSidebar();
const router = useRouter();
const { authState } = useFrontegg();
const frontegg = useFrontegg();
const servicesStore = useServicesStore();
const loaderStore = useLoaderStore();
const userStore = useUserStore();

const tenants = computed(() => userStore.allTenants || userStore.tenants || []);

// Flatten tenants with indentation for display
const searchTerm = ref('');
const shownTenants = computed(() => tenants.value);

// Flatten tenant hierarchy for display
const flattenedTenants = computed(() => {
    const result = [];

    const flatten = (tenantList, level = 0) => {
        if (!tenantList || !Array.isArray(tenantList)) return;

        tenantList.forEach((tenant) => {
            result.push({
                ...tenant,
                level,
                displayName: tenant.tenant_name
            });

            // Add children with increased indentation level
            if (tenant.children && Array.isArray(tenant.children) && tenant.children.length > 0) {
                flatten(tenant.children, level + 1);
            }
        });
    };

    flatten(shownTenants.value);
    return result;
});

// Filter flattened tenants with search term
const filteredTenants = computed(() => {
    const term = searchTerm.value.trim().toLowerCase();
    if (!term) return flattenedTenants.value;

    return flattenedTenants.value.filter((tenant) => tenant.tenant_name.toLowerCase().includes(term));
});

// Selected tenant logic
const selectedTenantId = ref(null);
const selectedTenant = computed(() => {
    const tenantId = selectedTenantId.value || authState.user?.tenantId;
    const allTenants = tenants.value;
    function findTenant(list, id) {
        for (const t of list) {
            if (t.tenant_id === id) return t;
            if (t.children) {
                const found = findTenant(t.children, id);
                if (found) return found;
            }
        }
        return null;
    }
    return findTenant(allTenants, tenantId) || {};
});

// Tenant switch action
function switchOrganisation(tenantId) {
    if (selectedTenant.value.tenant_id !== tenantId) {
        selectedTenantId.value = tenantId;
        // show spinner while switching tenant
        loaderStore.activateLoading('tenant.switch');
        router.push({ name: 'dashboard', query: {} });
        frontegg.fronteggAuth.tenantsActions.switchTenant({
            tenantId,
            callback: () => {
                loaderStore.deactivateLoading('tenant.switch');
                setTimeout(() => window.location.reload(), 100);
            }
        });
    }
}

function actionToDo(tenantId) {
    switchOrganisation(tenantId);
}

const enableTenantSwitch = computed(() => flattenedTenants.value.length > 1);
// show spinner when switching tenant
const generalIsLoading = computed(
    () => loaderStore.isLoading('tenant.switch') || !selectedTenant.value?.tenant_name || loaderStore.isLoading('services.fetch')
);

// Ensure tenants are fetched
onMounted(async () => {
    if (!userStore.tenants) {
        await userStore.fetchTenantTree();
    }
});
</script>

<template>
    <div class="relative inline-block w-full">
        <DropdownMenu v-if="enableTenantSwitch">
            <DropdownMenuTrigger as-child>
                <SidebarMenuButton size="lg" class="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground">
                    <div class="flex aspect-square size-8 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground">
                        <template v-if="props.showStreamkapIcon">
                            <img :src="streamkapIconWhite" class="size-4" alt="Streamkap" />
                        </template>
                        <template v-else>
                            <ChevronsUpDown class="size-4" />
                        </template>
                    </div>
                    <SkeletonLoader preset="nav-item" :loading="generalIsLoading">
                        <div class="grid flex-1 text-left text-sm leading-tight">
                            <span class="truncate font-semibold">{{ selectedTenant.tenant_name }}</span>
                            <Badge
                                v-if="props.showServiceBadge && !loaderStore.isLoading('services.fetch') && servicesStore.current"
                                v-can-access.permissions="$Permissions.READ_SERVICES"
                                variant="outline"
                                class="mt-0.5 w-full px-1.5 py-0.5 text-xs rounded-md text-sidebar-primary-foreground bg-sidebar-primary peer-hover/menu-button:bg-sidebar-accent peer-hover/menu-button:text-sidebar-accent-foreground peer-data-[state=open]/menu-button:bg-sidebar-accent peer-data-[state=open]/menu-button:text-sidebar-accent-foreground">
                                {{ servicesStore.current?.name }}
                            </Badge>
                        </div>
                    </SkeletonLoader>
                    <ChevronsUpDown class="ml-auto size-4" />
                </SidebarMenuButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent
                class="w-[--reka-dropdown-menu-trigger-width] min-w-56 rounded-lg"
                align="start"
                :side="isMobile ? 'bottom' : 'right'"
                :side-offset="4">
                <DropdownMenuLabel class="p-2">
                    <Input v-model="searchTerm" placeholder="Search tenants..." @keydown.stop />
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <div class="py-1">
                    <template v-for="tenant in filteredTenants" :key="tenant.tenant_id">
                        <DropdownMenuItem
                            @click="actionToDo(tenant.tenant_id)"
                            :class="[
                                'cursor-pointer transition-colors',
                                tenant.tenant_id === selectedTenant.tenant_id
                                    ? 'bg-sidebar-accent text-sidebar-accent-foreground'
                                    : 'hover:bg-sidebar-accent/50'
                            ]"
                            :style="{
                                paddingLeft: `${12 + tenant.level * 20}px`,
                                paddingRight: '12px',
                                minHeight: '36px'
                            }">
                            <div class="flex items-center w-full">
                                <span
                                    :class="[
                                        'truncate',
                                        {
                                            'text-sm text-muted-foreground font-normal': tenant.level > 0,
                                            'text-sm font-semibold': tenant.level === 0
                                        }
                                    ]">
                                    {{ tenant.tenant_name }}
                                </span>
                            </div>
                        </DropdownMenuItem>
                    </template>
                    <template v-if="filteredTenants.length === 0">
                        <DropdownMenuItem disabled class="text-muted-foreground"> No tenants found </DropdownMenuItem>
                    </template>
                </div>
            </DropdownMenuContent>
        </DropdownMenu>
        <SidebarMenuButton v-else size="lg" class="cursor-not-allowed pointer-events-none">
            <div class="flex aspect-square size-8 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground">
                <template v-if="props.showStreamkapIcon">
                    <img :src="streamkapIconWhite" class="size-4" alt="Streamkap" />
                </template>
                <template v-else>
                    <ChevronsUpDown class="size-4" />
                </template>
            </div>
            <SkeletonLoader preset="nav-label" :loading="generalIsLoading">
                <div class="grid flex-1 text-left text-sm leading-tight">
                    <span class="truncate font-semibold">{{ selectedTenant.tenant_name }}</span>
                    <Badge
                        v-if="props.showServiceBadge && !loaderStore.isLoading('services.fetch') && servicesStore.current"
                        v-can-access.permissions="$Permissions.READ_SERVICES"
                        variant="outline"
                        class="mt-0.5 w-full px-1.5 py-0.5 text-xs rounded-md text-sidebar-primary-foreground bg-sidebar-primary">
                        {{ servicesStore.current?.name }}
                    </Badge>
                </div>
            </SkeletonLoader>
        </SidebarMenuButton>
    </div>
</template>
